import { message } from "antd";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { authService, cartService } from "@/services";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearAuth,
  setAuthSession,
  setAuthStatus,
  setTenantContext,
  setUserProfile,
} from "@/store/auth.slice";
import { clearServerCount, setServerCount } from "@/store/cart.slice";
import type {
  AuthSession,
  ForgotPasswordRequest,
  ForgotPasswordVerify,
  ResetPasswordPayload,
} from "@/types";
import { normalizeApiError } from "@/utils/api-error";

export const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  const localItemCount = useAppSelector((state) =>
    state.cart.items.reduce((sum, item) => sum + item.quantity, 0),
  );

  const completeAuthSession = useCallback(
    async (session: AuthSession) => {
      dispatch(setAuthSession(session));

      try {
        await cartService.mergeCart();
        const cartCount = await cartService.fetchCartCount();
        dispatch(
          setServerCount({
            count: cartCount.count,
            syncedLocalCount: localItemCount,
          }),
        );
      } catch {
        dispatch(clearServerCount());
        message.warning(
          "Đăng nhập thành công nhưng chưa đồng bộ được giỏ hàng.",
        );
      }
    },
    [dispatch, localItemCount],
  );

  const checkAuth = useCallback(async () => {
    if (!authState.accessToken) {
      dispatch(setAuthStatus("anonymous"));
      return null;
    }

    dispatch(setAuthStatus("loading"));

    try {
      const profile = await authService.getProfile();
      dispatch(setUserProfile(profile.user));
      dispatch(setTenantContext(profile.tenant));
      dispatch(setAuthStatus("authenticated"));
      return profile;
    } catch {
      dispatch(clearAuth());
      return null;
    }
  }, [authState.accessToken, dispatch]);

  const logout = useCallback(
    async (options?: { redirectToHome?: boolean }) => {
      try {
        await authService.logout();
      } catch {
        // Ignore network failures on logout; clear the local session regardless.
      } finally {
        dispatch(clearAuth());
        dispatch(clearServerCount());

        if (options?.redirectToHome !== false) {
          navigate(ROUTES.HOME);
        }
      }
    },
    [dispatch, navigate],
  );

  const requestPasswordReset = useCallback(
    async (payload: ForgotPasswordRequest) => {
      try {
        const challenge = await authService.requestPasswordReset(payload);
        return { success: true as const, challenge };
      } catch (unknownError) {
        return {
          success: false as const,
          error: normalizeApiError(
            unknownError,
            "Không thể gửi mã OTP khôi phục mật khẩu.",
          ),
        };
      }
    },
    [],
  );

  const verifyPasswordResetOtp = useCallback(
    async (payload: ForgotPasswordVerify) => {
      try {
        const verification = await authService.verifyPasswordResetOtp(payload);
        return { success: true as const, verification };
      } catch (unknownError) {
        return {
          success: false as const,
          error: normalizeApiError(unknownError, "Không thể xác minh mã OTP."),
        };
      }
    },
    [],
  );

  const resetPassword = useCallback(async (payload: ResetPasswordPayload) => {
    try {
      const result = await authService.resetPassword(payload);
      return { success: true as const, result };
    } catch (unknownError) {
      return {
        success: false as const,
        error: normalizeApiError(unknownError, "Không thể đặt lại mật khẩu."),
      };
    }
  }, []);

  return {
    ...authState,
    checkAuth,
    completeAuthSession,
    logout,
    requestPasswordReset,
    verifyPasswordResetOtp,
    resetPassword,
  };
};
